<template>
  <div>
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mt-5 pt-5 ps-3">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item">
          <router-link to="/">Home</router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link to="/admin/knowledge-bases">Knowledge Bases</router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">
          {{ isEditMode ? "Edit" : "New" }}
        </li>
      </ol>
    </nav>

    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-8 col-md-10 mx-auto">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-1 fw-bold">
                {{
                  isEditMode ? "Edit Knowledge Base" : "Create Knowledge Base"
                }}
              </h5>
              <small class="text-muted">
                {{
                  isEditMode
                    ? "Update knowledge base details"
                    : "Create a new knowledge base for your documents"
                }}
              </small>
            </div>

            <div class="card-body">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center py-4">
                <div class="spinner-border loading-spinner text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">Loading knowledge base...</p>
              </div>

              <!-- Form -->
              <form v-else @submit.prevent="handleSubmit">
                <!-- Name Field -->
                <div class="mb-3">
                  <label for="name" class="form-label fw-bold">
                    Name <span class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    id="name"
                    v-model="formData.name"
                    :class="{ 'is-invalid': errors.name }"
                    placeholder="Enter knowledge base name"
                    required
                  />
                  <div v-if="errors.name" class="invalid-feedback">
                    {{ errors.name }}
                  </div>
                  <small class="form-text text-muted">
                    A descriptive name for your knowledge base
                  </small>
                </div>

                 <!-- Category Field -->
                <div class="mb-3">
                  <label for="category" class="form-label fw-bold">
                    Category <span class="text-danger">*</span>
                  </label>
                  <select
                    id="category"
                    class="form-select"
                    v-model="formData.category"
                    :class="{ 'is-invalid': errors.category }"
                    required
                  >
                    <option value="" disabled>Select a category</option>
                    <option
                      v-for="cat in kbCategories"
                      :key="cat.value"
                      :value="cat.value"
                    >
                      {{ cat.label }}
                    </option>
                  </select>
                  <div v-if="errors.category" class="invalid-feedback">
                    {{ errors.category }}
                  </div>
                  <small class="form-text text-muted">
                    Categorize your knowledge base for better organization
                  </small>
                </div>

                <!-- Description Field -->
                <div class="mb-3">
                  <label for="description" class="form-label fw-bold">
                    Description
                  </label>
                  <textarea
                    class="form-control"
                    id="description"
                    v-model="formData.description"
                    :class="{ 'is-invalid': errors.description }"
                    rows="4"
                    placeholder="Enter a description for this knowledge base"
                  ></textarea>
                  <div v-if="errors.description" class="invalid-feedback">
                    {{ errors.description }}
                  </div>
                  <small class="form-text text-muted">
                    Optional: Describe the purpose and contents of this
                    knowledge base
                  </small>
                </div>

               

                

                <!-- Active Status -->
                <div class="mb-3">
                  <div class="form-check form-switch px-0">
                    <input
                      class="form-check-input ms-1"
                      type="checkbox"
                      id="isActive"
                      v-model="formData.isActive"
                    />
                    <label class="form-check-label ps-2" for="isActive">
                      Active
                    </label>
                  </div>
                  <small class="form-text text-muted">
                    Only active knowledge bases can be used for document
                    retrieval
                  </small>
                </div>

                <!-- Error Message -->
                <div v-if="submitError" class="alert alert-danger" role="alert">
                  <i class="fa fa-exclamation-triangle me-2"></i>
                  {{ submitError }}
                </div>

                <!-- Action Buttons -->
                <div class="d-flex gap-2 justify-content-end">
                  <router-link
                    to="/admin/knowledge-bases"
                    class="btn btn-outline-secondary"
                  >
                    <i class="fa fa-times me-1"></i>
                    Cancel
                  </router-link>
                  <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="isSaving"
                  >
                    <span v-if="isSaving">
                      <span
                        class="spinner-border spinner-border-sm me-1"
                        role="status"
                      ></span>
                      Saving...
                    </span>
                    <span v-else>
                      <i class="fa fa-save me-1"></i>
                      {{ isEditMode ? "Update" : "Create" }}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import apiService from "@/services/apiService";

// Router
const router = useRouter();
const route = useRoute();

// Computed
const isEditMode = computed(() => !!route.params.id);
const knowledgeBaseId = computed(() => route.params.id as string);

type MetaKey =
  | "capabilities"
  | "roles"
  | "competencies"
  | "rules"
  | "skills"
  | "levels";

// Reactive data
const formData = reactive({
  name: "",
  description: "",
  category: "",
  isActive: true, 
});
const kbCategories = ref<any[]>([]);
const loadCategories = async () => {
  try {
    const response = await apiService.get("/knowledge-base-categories");

    // Adjust depending on API response structure
    kbCategories.value = response?.data?.map((cat: any) => ({
      label: cat.name,
      value: cat.slug || cat._id || cat.name,
    })) || [];

  } catch (error) {
    console.error("Failed to load KB categories", error);
  }
};
 

const metaOptions = reactive<Record<string, any[]>>({
  capabilities: [],
  roles: [],
  competencies: [],
  rules: [],
  skills: [],
  levels: [],
});

const activeTab = ref<MetaKey>("capabilities");
const isMetaLoading = ref(false);

const errors = reactive<Record<string, string>>({});
const isLoading = ref(false);
const isSaving = ref(false);
const submitError = ref<string | null>(null);

// Methods
const loadKnowledgeBase = async () => {
  if (!isEditMode.value) return;

  isLoading.value = true;
  try {
    const response = await apiService.getKnowledgeBaseById(
      knowledgeBaseId.value
    );

    // Populate form with existing data
    if (response) {
      formData.name = response.name || "";
      formData.description = response.description || "";
      formData.category = response.category || "";
      formData.isActive = response.isActive ?? true; 
    }
  } catch (err: any) {
    console.error("Error loading knowledge base:", err);
    showToast(
      err?.response?.data?.message || "Failed to load knowledge base",
      "error"
    );
    // Redirect back if not found
    router.push("/admin/knowledge-bases");
  } finally {
    isLoading.value = false;
  }
};

 

const validateForm = (): boolean => {
  // Clear previous errors
  Object.keys(errors).forEach((key) => delete errors[key]);

  let isValid = true;

  // Validate name
  if (!formData.name.trim()) {
    errors.name = "Name is required";
    isValid = false;
  } else if (formData.name.length < 3) {
    errors.name = "Name must be at least 3 characters";
    isValid = false;
  }

  // Validate description (optional, but if provided, check length)
  if (formData.description && formData.description.length > 1000) {
    errors.description = "Description must not exceed 1000 characters";
    isValid = false;
  }

  // Validate category
  if (!formData.category) {
    errors.category = "Category is required";
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  submitError.value = null;

  // Validate form
  if (!validateForm()) {
    return;
  }

  isSaving.value = true;

  try {
    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      category: formData.category,
      isActive: formData.isActive, 
    };

    if (isEditMode.value) {
      // Update existing knowledge base
      await apiService.updateKnowledgeBase(knowledgeBaseId.value, payload);
      showToast("Knowledge base updated successfully", "success");
      router.push(`/admin/knowledge-bases/${knowledgeBaseId.value}`);
    } else {
      // Create new knowledge base
      const response = await apiService.createKnowledgeBase(payload);
      showToast("Knowledge base created successfully", "success");

      // Redirect to the new knowledge base details page
      if (response._id || response.id) {
        router.push(`/admin/knowledge-bases/${response._id || response.id}`);
      } else {
        router.push("/admin/knowledge-bases");
      }
    }
  } catch (err: any) {
    console.error("Error saving knowledge base:", err);
    const errorMessage =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to save knowledge base";
    submitError.value = errorMessage;
    showToast(errorMessage, "error");
  } finally {
    isSaving.value = false;
  }
};

const showToast = (message: string, type: "success" | "error" | "info") => {
  // Simple toast implementation - you can replace with your toast library
  console.log(`[${type.toUpperCase()}] ${message}`);

  // You can integrate with a toast library like vue-toastification here
  // For now, using alert for errors only
  if (type === "error") {
    // alert(message);
  }
};

// Lifecycle
onMounted(() => {
  loadCategories(); 
  if (isEditMode.value) {
    loadKnowledgeBase();
  }
});
</script>

<style scoped>
.breadcrumb-item a {
  text-decoration: none;
  color: #6c757d;
}

.breadcrumb-item a:hover {
  color: #495057;
}

.loading-spinner {
  width: 30px;
  height: 30px;
}

.form-control.is-invalid {
  border-color: #dc3545;
  padding-right: calc(1.5em + 0.75rem);
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.form-check-input {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
}

/* Custom styles for meta tabs */
.nav-tabs .nav-link {
  border: none;
  color: #6c757d;
  font-weight: 500;
  padding: 0.75rem 1.25rem;
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link:hover {
  color: #35bfbf;
  background-color: transparent;
  border-color: transparent;
}

.nav-tabs .nav-link.active {
  color: #35bfbf;
  background-color: transparent;
  border-color: transparent;
  border-bottom: 2px solid #35bfbf;
}

.tab-content {
  border: 1px solid #dee2e6;
  transition: all 0.3s ease;
}

.tab-content::-webkit-scrollbar {
  width: 6px;
}

.tab-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.tab-content::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 10px;
}

.tab-content::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.form-check {
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.form-check:hover {
  background-color: #e9ecef;
}

.form-check-input:checked + .form-check-label {
  color: #35bfbf;
  font-weight: 600;
}
</style>
